import { useState } from "react";
import { CheckCircle2, Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import Reveal from "@/components/Reveal";
import { SectionLabel } from "@/components/common";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const agentRanges = ["1–5", "6–20", "21–100", "100+"];

export default function EarlyAccess() {
  const [form, setForm] = useState({ full_name: "", work_email: "", company: "", building: "", agent_count: "" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = "Please enter your full name.";
    if (!form.work_email.trim()) e.work_email = "Work email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.work_email)) e.work_email = "Enter a valid email address.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await api.post("/waitlist", { ...form, source: "early-access" });
      setDone(true);
      toast.success("You're on the list.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Submission failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-[80vh] vora-hero-radial">
      <div className="absolute inset-0 vora-grid-bg opacity-40" aria-hidden />
      <section className="relative mx-auto max-w-3xl px-5 sm:px-8 py-24">
        <Reveal className="text-center">
          <SectionLabel>Early Access</SectionLabel>
          <h1 className="mt-4 text-4xl sm:text-5xl font-bold tracking-tight">
            Be early to the payment layer for AI agents.
          </h1>
        </Reveal>

        <Reveal delay={0.08}>
          <div className="mt-10 vora-glass rounded-3xl p-6 sm:p-9">
            {done ? (
              <div className="text-center py-10" data-testid="early-access-success">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-500/15 text-emerald-400">
                  <CheckCircle2 className="h-8 w-8" />
                </div>
                <h2 className="mt-6 text-2xl font-semibold text-white">You're on the list.</h2>
                <p className="mt-3 text-slate-400">
                  Thanks for your interest in Vvvoraa. We'll reach out to <span className="text-white">{form.work_email}</span> as early access opens.
                </p>
                <p className="mt-4 text-xs text-slate-500">
                  Demo submission — your details are stored for the waitlist. No automated emails are sent.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="early-access-form" noValidate>
                <div>
                  <Label htmlFor="full_name" className="text-slate-300">Full Name</Label>
                  <Input
                    id="full_name"
                    value={form.full_name}
                    onChange={(e) => set("full_name", e.target.value)}
                    className="mt-1.5 bg-white/[0.03] border-white/12 text-white placeholder:text-slate-500 focus-visible:ring-[#4F8CFF]"
                    placeholder="Ada Lovelace"
                    data-testid="input-full-name"
                  />
                  {errors.full_name && <p className="mt-1.5 text-xs text-red-400" data-testid="error-full-name">{errors.full_name}</p>}
                </div>

                <div>
                  <Label htmlFor="work_email" className="text-slate-300">Work Email</Label>
                  <Input
                    id="work_email"
                    type="email"
                    value={form.work_email}
                    onChange={(e) => set("work_email", e.target.value)}
                    className="mt-1.5 bg-white/[0.03] border-white/12 text-white placeholder:text-slate-500 focus-visible:ring-[#4F8CFF]"
                    placeholder="you@company.com"
                    data-testid="input-work-email"
                  />
                  {errors.work_email && <p className="mt-1.5 text-xs text-red-400" data-testid="error-work-email">{errors.work_email}</p>}
                </div>

                <div>
                  <Label htmlFor="company" className="text-slate-300">Company</Label>
                  <Input
                    id="company"
                    value={form.company}
                    onChange={(e) => set("company", e.target.value)}
                    className="mt-1.5 bg-white/[0.03] border-white/12 text-white placeholder:text-slate-500 focus-visible:ring-[#4F8CFF]"
                    placeholder="Acme AI"
                    data-testid="input-company"
                  />
                </div>

                <div>
                  <Label htmlFor="building" className="text-slate-300">What are you building?</Label>
                  <Textarea
                    id="building"
                    value={form.building}
                    onChange={(e) => set("building", e.target.value)}
                    className="mt-1.5 bg-white/[0.03] border-white/12 text-white placeholder:text-slate-500 focus-visible:ring-[#4F8CFF] min-h-[96px]"
                    placeholder="Autonomous research agents that purchase APIs and tools…"
                    data-testid="input-building"
                  />
                </div>

                <div>
                  <Label className="text-slate-300">Estimated number of AI agents</Label>
                  <Select value={form.agent_count} onValueChange={(v) => set("agent_count", v)}>
                    <SelectTrigger className="mt-1.5 bg-white/[0.03] border-white/12 text-white focus:ring-[#4F8CFF]" data-testid="select-agent-count">
                      <SelectValue placeholder="Select a range" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0B1526] border-white/10 text-white">
                      {agentRanges.map((r) => (
                        <SelectItem key={r} value={r} className="focus:bg-white/5">{r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button type="submit" disabled={loading} className="w-full h-12 bg-[#4F8CFF] hover:bg-[#3f7af0] text-white rounded-full font-semibold" data-testid="submit-early-access">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Request Early Access"}
                </Button>
              </form>
            )}
          </div>
        </Reveal>

        {/* Pricing */}
        <Reveal delay={0.12}>
          <div className="mt-10 vora-card rounded-3xl p-8 text-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-[#7C5CFF]/25 bg-[#7C5CFF]/8 px-3.5 py-1.5">
              <Sparkles className="h-3.5 w-3.5 text-[#7C5CFF]" />
              <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-[#b7a4ff]">Pricing</span>
            </span>
            <h2 className="mt-5 text-2xl font-semibold text-white">Early Access · Coming Soon</h2>
            <p className="mt-3 text-slate-400 max-w-md mx-auto">
              Pricing will be announced as Vvvoraa approaches public availability.
            </p>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
